import { httpClient } from "@/shared/api";

import type { CompanyPhoto, CompanyProfile, CompanyProfileInput, PhotoOrderItem } from "../model/types";
import type {
  CompanyResponseDTO,
  ListPhotosResponseDTO,
  PhotoDTO,
  UploadPhotoResponseDTO,
  UpdateCompanyRequestDTO,
  ReorderRequestDTO,
} from "./dto";

function dtoToProfile(dto: CompanyResponseDTO): CompanyProfile {
  return {
    id: dto.id,
    name: dto.name,
    address: dto.address,
    phone: dto.phone,
    timezone: dto.timezone,
    description: dto.description,
  };
}

function dtoToPhoto(dto: PhotoDTO): CompanyPhoto {
  return { id: dto.id, url: dto.url, position: dto.position };
}

/** Параметри upload-у: signal для abort. */
export interface UploadPhotoOpts {
  signal?: AbortSignal;
}

export const companyApi = {
  /** GET /api/company → CompanyProfile. */
  async getCompany(): Promise<CompanyProfile> {
    const { data } = await httpClient.get<CompanyResponseDTO>("/company");
    return dtoToProfile(data);
  },

  /** PATCH /api/company (full-replace, "" = clear) → CompanyProfile. */
  async updateCompany(input: CompanyProfileInput): Promise<CompanyProfile> {
    const body: UpdateCompanyRequestDTO = {
      name: input.name,
      address: input.address,
      phone: input.phone,
      description: input.description,
    };
    const { data } = await httpClient.patch<CompanyResponseDTO>("/company", body);
    return dtoToProfile(data);
  },

  /** GET /api/company/photos → CompanyPhoto[]. */
  async listPhotos(): Promise<CompanyPhoto[]> {
    const { data } = await httpClient.get<ListPhotosResponseDTO>("/company/photos");
    return data.photos.map(dtoToPhoto);
  },

  /** POST /api/company/photos (multipart, CSRF) → 201 CompanyPhoto. */
  async uploadPhoto(file: File, opts?: UploadPhotoOpts): Promise<CompanyPhoto> {
    const form = new FormData();
    form.append("photo", file);
    const { data } = await httpClient.post<UploadPhotoResponseDTO>("/company/photos", form, {
      signal: opts?.signal,
    });
    return dtoToPhoto(data.photo);
  },

  /** DELETE /api/company/photos/{id} (CSRF) → void. */
  async deletePhoto(id: string): Promise<void> {
    await httpClient.delete(`/company/photos/${id}`);
  },

  /** PUT /api/company/photos/order (CSRF) → CompanyPhoto[]. */
  async reorderPhotos(order: PhotoOrderItem[]): Promise<CompanyPhoto[]> {
    const body: ReorderRequestDTO = { order };
    const { data } = await httpClient.put<ListPhotosResponseDTO>("/company/photos/order", body);
    return data.photos.map(dtoToPhoto);
  },
};
